import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch } from "../store";
import { useAuthentication } from "./AuthenticationContext";
import { AuthenticateUserAction } from "../store/Auth/ActionCreator";
import styled from "styled-components";
import LoadingPage from "../components/LoadingPage/LoadingPage";

interface ISignInCallback {
  returnUrl?: string;
}

export enum SignInCallbackProcessingState {
  Idle = 'idle',
  Processing = 'processing',
  Completed = 'completed',
  Error = 'error',
}

export const getSignInCallbackMessage = (
  processingState: SignInCallbackProcessingState,
  errorMessage: string
): string => {
  if (processingState === SignInCallbackProcessingState.Error) {
    return errorMessage || 'Login error occurred';
  }
  return '';
};

export const SignInCallback: React.FC<ISignInCallback> = ({ returnUrl = "/" }) => {
  const auth = useAuthentication();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [processingState, setProcessingState] = useState<SignInCallbackProcessingState>(
    SignInCallbackProcessingState.Idle
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const processingRef = useRef<boolean>(false);
  const mountTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Prevent multiple callback processing with ref-based approach
    if (processingRef.current || processingState !== SignInCallbackProcessingState.Idle) {
      console.log("[SignInCallback] Callback processing already started or completed, skipping");
      return;
    }
    
    const processCallback = async () => {
      processingRef.current = true;
      setProcessingState(SignInCallbackProcessingState.Processing);
      
      try {
        console.log("[SignInCallback] Processing signin callback...", {
          mountTime: mountTimeRef.current,
          currentTime: Date.now(),
          returnUrl
        });
        
        // Add a small delay to ensure any concurrent processing is detected
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const redirectUri = await auth.finishSignIn();
        console.log("[SignInCallback] Signin callback completed successfully");
        
        // Update Redux state with new authentication
        console.log("[SignInCallback] Re-triggering authentication check in Redux...");
        dispatch(AuthenticateUserAction());
        
        setProcessingState(SignInCallbackProcessingState.Completed);
        
        // Navigate back to the original page (from OIDC state) or the fallback returnUrl
        navigate(redirectUri || returnUrl);
        
      } catch (error: any) {
        console.error("[SignInCallback] Error in signin callback:", error);
        setProcessingState(SignInCallbackProcessingState.Error);
        
        // Enhanced error handling based on error type
        if (error?.name === 'AuthenticationCodeError') {
          // Code was already consumed (e.g. reload during callback).
          // Navigate home so AppRouting triggers a clean login flow.
          setErrorMessage('Authentication session expired. Please sign in again.');
          await new Promise(resolve => setTimeout(resolve, 2500));
          navigate('/');
        } else if (error?.message?.includes('already in progress')) {
          console.log("[SignInCallback] Duplicate callback detected, waiting for original to complete");
          setErrorMessage('Authentication in progress...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          try {
            if (auth.isAuthenticated) {
              dispatch(AuthenticateUserAction());
              setProcessingState(SignInCallbackProcessingState.Completed);
              navigate(returnUrl);  // fallback: redirect URI not available on duplicate path
            } else {
              navigate('/');
            }
          } catch {
            navigate('/');
          }
        } else {
          setErrorMessage('An error occurred during sign in. Please try again.');
          await new Promise(resolve => setTimeout(resolve, 2500));
          navigate('/');
        }
      }
    };

    // Small delay to handle React strict mode double execution
    const timeoutId = setTimeout(processCallback, 50);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [auth, navigate, returnUrl, dispatch]); // Removed processingState from dependencies to prevent loops

  const message = getSignInCallbackMessage(processingState, errorMessage);

  return (
    <LoadingContainer>
      <LoadingPage message="Processing authentication..." />
      {message && <Message>{message}</Message>}
    </LoadingContainer>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
`;

const Message = styled.div`
  margin-top: 20px;
  font-size: 16px;
  font-weight: bold;
  color: #dc3545;
`;

export default SignInCallback;
